import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const item = newsList.find((news) => news.id === id);

  usePageMeta(
    item ? `新闻：${item.title} - 濑户环奈官网` : "新闻未找到 - 濑户环奈官网",
    item ? item.summary : "未找到对应的新闻动态。",
  );

  if (!item) {
    return (
      <PageShell>
        <div className="w-full px-4 md:px-8 py-24">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-5">
              <i className="ri-newspaper-line"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              未找到该新闻动态
            </h1>
            <p className="text-sm text-foreground-600 mb-6">该新闻可能已被移除或链接有误。</p>
            <Link
              to="/news"
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部动态
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const related = newsList
    .filter(
      (news) =>
        news.id !== item.id &&
        (news.category === item.category || news.tags.some((tag) => item.tags.includes(tag))),
    )
    .slice(0, 3);

  const fallbackRelated =
    related.length > 0 ? related : newsList.filter((news) => news.id !== item.id).slice(0, 3);

  return (
    <PageShell>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "新闻动态", to: "/news" },
              { label: "正文" },
            ]}
          />
        </div>
      </div>

      <article className="w-full px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
              {item.category}
            </span>
            <span className="text-xs text-foreground-500">{item.date}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 leading-snug">
            {item.title}
          </h1>

          <div className="w-full rounded-lg overflow-hidden bg-background-200 border border-background-200 mb-8">
            <img
              src={item.cover}
              alt={`新闻配图：${item.title}`}
              title={`${item.title} - 濑户环奈新闻动态`}
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="flex flex-col gap-5 mb-8">
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-10 pb-8 border-b border-background-200">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      <div className="w-full px-4 md:px-8 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              相关文章
            </h2>
            <Link
              to="/news"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              返回列表
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="flex flex-col gap-4">
            {fallbackRelated.map((news) => (
              <NewsCard key={news.id} item={news} />
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}